/**
 * TopNav — storefront-mode top bar. Per design-contract §7 + cycle 2 brief §3
 * stage 3, reduced per brief-13-shell-nav-rail (SHELL-01): hub-like modes
 * (vertical-ref, tier-1-hub) now use NavRail as primary navigation instead —
 * see src/shell/AppShell.tsx. This component only ever renders for the two
 * storefront modes below.
 *
 * Mode layout:
 *  tier-0:       wordmark | Dashboard | Marketplace
 *  reference:    wordmark only (no nav items, no auth chrome)
 *
 * Domain-agnosticism: no hostname strings. All navigation is hash-based or
 * derived from window.location.origin via keycloak logout.
 */

import { useConfig } from '../config/ConfigContext';
import { t } from '../i18n';
import type { Route } from '../shell/Router';
import { isAuthenticatedBroker } from '../config/schema';
import Wordmark from './Wordmark';

interface TopNavProps {
  activePath: Route;
  onNavigate: (path: Route) => void;
  cartCount?: number;
}

// ── TopNav ────────────────────────────────────────────────────────────────────

export default function TopNav({ activePath, onNavigate }: TopNavProps) {
  const config = useConfig();
  const mode = config.instance.mode;

  // reference mode: wordmark only, no nav, no auth chrome (contract §7)
  if (mode === 'reference') {
    return (
      <nav className="dp-shell__nav" aria-label="Main navigation">
        <Wordmark />
      </nav>
    );
  }

  // Hub-like modes no longer render through TopNav — AppShell renders NavRail
  // for them instead (brief-13-shell-nav-rail, SHELL-01). Since the mode split
  // that includes the tenant broker: both brokers carry the same chrome, and what
  // differs is which routes their mode registers
  // (decision-2026-07-25-broker-mode-split). Guard defensively rather than assume
  // the caller always branches correctly.
  const isHubLike = isAuthenticatedBroker(mode) || mode === 'tier-1-hub';
  if (isHubLike) return null;

  const navItems: Array<{ label: string; path: Route }> =
    mode === 'tier-0'
      ? [
          { label: t('nav.dashboard'), path: '/dashboard' },
          { label: t('nav.marketplace'), path: '/' },
        ]
      : [];

  return (
    <nav className="dp-shell__nav" aria-label="Main navigation">
      <Wordmark />

      {navItems.length > 0 && (
        <ul
          role="list"
          style={{
            display: 'flex',
            gap: 'var(--sp-6)',
            listStyle: 'none',
            padding: 0,
            margin: 0,
            flex: 1,
          }}
        >
          {navItems.map(({ label, path }) => {
            const isActive = activePath === path;
            return (
              <li key={path}>
                <button
                  onClick={() => onNavigate(path)}
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    fontFamily: 'var(--font-sans)',
                    fontSize: 'var(--fs-body-sm)',
                    fontWeight: 500,
                    color: isActive ? 'var(--fg-on-navy)' : 'var(--fg-on-navy-muted)',
                    padding: '0 0 2px',
                    borderBottom: isActive
                      ? '2px solid var(--dp-red-500)'
                      : '2px solid transparent',
                    transition: 'color var(--dur-fast) var(--ease-out)',
                  }}
                  onMouseEnter={(e) => {
                    if (!isActive)
                      (e.currentTarget as HTMLButtonElement).style.color =
                        'var(--fg-on-navy)';
                  }}
                  onMouseLeave={(e) => {
                    if (!isActive)
                      (e.currentTarget as HTMLButtonElement).style.color =
                        'var(--fg-on-navy-muted)';
                  }}
                  aria-current={isActive ? 'page' : undefined}
                >
                  {label}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </nav>
  );
}
